import { useState, useEffect, useRef } from 'react';
import { MapPin, Check, Loader2, AlertCircle, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import API from '@/lib/api';

// Google-backed address field with mobile-safe suggestions dropdown.
// `value` is an object: { raw, verified, manual_entry, formatted_address, postal_address }
export function AddressAutocomplete({ value, onChange, testId = 'signup-address' }) {
  const [query, setQuery] = useState(value?.formatted_address || value?.raw || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [flipUp, setFlipUp] = useState(false);
  const [serviceDown, setServiceDown] = useState(false);
  const skipFetch = useRef(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const verified = !!value?.verified;
  const manualEntry = !!value?.manual_entry;

  // Fetch autocomplete suggestions (debounced).
  useEffect(() => {
    if (skipFetch.current) { skipFetch.current = false; return; }
    if (verified) return;
    const q = query.trim();
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await API.post('/geo/address/autocomplete', { input: q });
        const list = res.data.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0);
        if (res.data.service_error) {
          setServiceDown(true);
        }
      } catch {
        setSuggestions([]);
        setServiceDown(true);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, verified]);

  // Close dropdown when tapping/clicking anywhere outside the wrapper.
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [open]);

  // Auto-flip the dropdown above the input if it would fall below the viewport.
  useEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setFlipUp(spaceBelow < 220 && rect.top > 220);
  }, [open, suggestions.length]);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setError('');
    onChange({ raw: v, verified: false, manual_entry: serviceDown && v.trim().length >= 5 });
  };

  // Re-emit the value when serviceDown flips so manual_entry propagates without
  // requiring the user to type an extra keystroke. This unblocks Continue.
  useEffect(() => {
    if (!serviceDown) return;
    if (verified) return;
    if (query.trim().length < 5) return;
    onChange({ raw: query, verified: false, manual_entry: true });
    // onChange identity from parent is stable enough for this signup flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDown, query, verified]);

  const handleFocus = () => {
    if (suggestions.length && !verified) setOpen(true);
    // On mobile, ensure the input is scrolled into view above the keyboard.
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const validate = async (addressLine) => {
    setVerifying(true); setError('');
    try {
      const res = await API.post('/geo/address/validate', { address_lines: [addressLine] });
      if (res.data.service_error) {
        setServiceDown(true);
        onChange({ raw: addressLine, verified: false, manual_entry: true });
        setError('');
        return;
      }
      if (res.data.valid) {
        skipFetch.current = true;
        setQuery(res.data.formatted_address || addressLine);
        onChange({
          raw: addressLine,
          verified: true,
          manual_entry: false,
          formatted_address: res.data.formatted_address,
          postal_address: res.data.postal_address,
        });
      } else {
        onChange({ raw: addressLine, verified: false, manual_entry: false });
        setError("We couldn't verify that address. Please pick one from the suggestions.");
      }
    } catch {
      setServiceDown(true);
      onChange({ raw: addressLine, verified: false, manual_entry: true });
    } finally { setVerifying(false); }
  };

  const selectSuggestion = (s) => {
    skipFetch.current = true;
    setOpen(false);
    setSuggestions([]);
    setQuery(s.description);
    validate(s.description);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          ref={inputRef}
          data-testid={testId}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="Start typing your address…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          inputMode="search"
          enterKeyHint="search"
          className={`h-10 mt-1 pl-9 pr-9 ${verified ? 'border-emerald-400 focus-visible:ring-emerald-400' : manualEntry ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {(loading || verifying) && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          {!loading && !verifying && verified && <Check className="w-4 h-4 text-emerald-600" data-testid="address-verified" />}
          {!loading && !verifying && !verified && manualEntry && <Info className="w-4 h-4 text-amber-500" data-testid="address-manual" />}
        </div>
      </div>

      {open && suggestions.length > 0 && (
        <div
          className={`absolute z-50 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto ${flipUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          data-testid="address-suggestions"
          role="listbox"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.place_id || i}
              type="button"
              data-testid="address-suggestion"
              role="option"
              onPointerDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
              className="w-full text-left px-3 py-3 hover:bg-slate-50 active:bg-slate-100 border-b border-slate-50 last:border-0 flex items-start gap-2 touch-manipulation"
            >
              <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
              <span className="text-xs">
                <span className="text-slate-800 font-medium">{s.main_text || s.description}</span>
                {s.secondary_text && <span className="text-slate-500 block">{s.secondary_text}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Loading / states — mobile-visible hints */}
      {!verified && loading && query.trim().length >= 3 && (
        <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1" data-testid="address-loading">
          <Loader2 className="w-3 h-3 animate-spin" /> Looking up addresses…
        </p>
      )}

      {/* Service degraded — allow manual entry */}
      {serviceDown && !verified && (
        <div className="mt-1.5 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-1.5" data-testid="address-service-down">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Address verification is temporarily unavailable. Enter your address as best you can — we&apos;ll verify it after signup.</span>
        </div>
      )}

      {!verified && !serviceDown && !loading && !error && query.trim().length >= 3 && suggestions.length === 0 && !verifying && (
        <p className="text-[11px] text-slate-400 mt-1.5">No matches — keep typing or check spelling.</p>
      )}

      {verified && !error && (
        <p className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1" data-testid="address-verified-note">
          <Check className="w-3 h-3" /> Address verified
        </p>
      )}
      {manualEntry && !verified && !error && (
        <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1" data-testid="address-manual-note">
          <Info className="w-3 h-3" /> Manual entry accepted — we&apos;ll verify after signup
        </p>
      )}
      {error && (
        <p className="text-[11px] text-red-500 mt-1.5 flex items-center gap-1" data-testid="address-error">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}
      {!verified && !error && query.trim().length > 0 && query.trim().length < 3 && (
        <p className="text-[11px] text-slate-400 mt-1.5">Keep typing to see suggestions…</p>
      )}
    </div>
  );
}
